import React, { useMemo, useCallback, useEffect, useRef, useState } from "react";
import { Animated, View, TextInput, Platform, Alert, TouchableOpacity, ImageBackground, Pressable, StyleSheet, useWindowDimensions } from "react-native";
import { Text, Title } from "@constants/Themed";
import { Checkbox, H3 } from "tamagui";
import { Card } from "@constants/Themed";
import { useDispatch, useSelector } from 'react-redux';
import styles from "@stylesheet";
import * as WebBrowser from 'expo-web-browser';
import useForm from '@hooks/useForm';
import loginUser from '@api/login';
import { BACK_TO_LOGIN, CURRENT_PAGE, DISMISSED_APP_INFO, FAILED, HIDE_ALL_MODALS, HIDE_LOADING_MODAL, HIDE_PASSWORD_RESET_MODAL, HIDE_REGISTER_MODAL, HOLD_REGISTER_STATE_VALUES, INITIALISE_MENU, LOGIN_FLIP, RESET_APP, SHOW_AUTH_MODAL, SHOW_LOADING_MODAL, SHOW_OTP, SHOW_PASSWORD_RESET_MODAL, SHOW_REGISTER_MODAL, SHOW_TC_MODAL, SUCCESS, SWITCH_COMPANY_REQUEST, TOGGLE_OTP, SET_LEARNING_MODE, CLEAR_LEARNING_MODE, CHANGE_MENU, STORE_BUSINESS_PROMPT, SKIP_ONBOARDING, UPDATE_HELD_PROMO_CODE } from '@reduxLocal/actions/types';
import { DefaultRootState, store } from "@reduxLocal/persistState";
import { Entypo, Ionicons, EvilIcons, FontAwesome6, FontAwesome, AntDesign } from "@expo/vector-icons";
import apis, { registerUser, companyCheck, passwordReset } from '@api/api';
import DropSelect from "../../../../components/Global/DropSelect";
import { PanGestureHandler, ScrollView } from "react-native-gesture-handler";
import registrationInfo from "../Components/RegistrationInfo";
import { useTranslation } from "react-i18next";
import OTPInput from "../Components/OTPInput";
import { LinearGradient } from "expo-linear-gradient";
import LottieView from '@utils/lottieAnimation';
import PasswordResetModal from "../Components/PasswordResetModal";
import { Button, XStack, YStack } from 'tamagui';
import ChangeLogFetcher from '../../../logFetch';
import { H1, H2, H5, H4, Input, SizableText } from "tamagui";

// Utility function to normalize border styles and prevent conflicts
const normalizeBorderStyles = (styles: any) => {
  if (!styles) return styles;

  const normalized = { ...styles };

  // If both borderColor and specific border colors are present, use specific ones
  if (normalized.borderColor && (normalized.borderTopColor || normalized.borderRightColor || normalized.borderBottomColor || normalized.borderLeftColor)) {
    // Remove the shorthand borderColor to avoid conflicts
    delete normalized.borderColor;
  }

  return normalized;
};
import { useRoute, useFocusEffect } from "@react-navigation/native";
import { CardBackground } from 'tamagui';
import LinkedInButton from "../Components/Authorisation/LinkedInButton";
import DiscordButton from "../Components/Authorisation/DiscordButton";
import Registration from "../Registration";
import { fetchAndValidateSession } from './utils/authSessionHandler';
import {
  completePasskeyLogin,
  getPasskeyUserId,
} from '@utils/completePasskeyLogin';
import {
  dispatchPostAuthEasyModeOnboarding,
  navigatePostEmailSignupToEasyMode,
} from '../Components/Authorisation/utils/authUtils';
import BackendStatusIndicator from './Components/BackendStatusIndicator';
import DynamicContentInfo from '../../../../components/login/components/DynamicContentInfo';
import { loadLoginScreenStyles } from '@utils/loadWebStyles';
import { activateLoginArt } from '@navigation/loginArt/loginArtTransition';
import LoginArtBackground from './Components/LoginArtBackground';
import { enableCapture } from '@services/easyModeActionCapture';
import { BlurView } from 'expo-blur';
import PromoCodeField from '@components/Global/PromoCodeField';
import { responsiveFontSize } from '@utils/responsiveFontSize';

const LOGIN_HERO_BG =
  Platform.OS === 'web'
    ? { uri: '/images/hero-bg.webp' }
    : require('@assets/backgrounds/hero-bg.png');

/** Fixed layout tokens — responsive behavior is handled in login-screen.web.css */
const WEB_LAYOUT = {
  spacing: { xs: 8, sm: 12, md: 16, lg: 24, xl: 32, xxl: 48 },
  typography: { xs: 12, sm: 14, base: 16, lg: 18, xl: 20, xxl: 24 },
  components: { cardRadius: 16, iconSize: 22, buttonHeight: 44, inputHeight: 44 },
} as const;

const PROMO_CARD_WIDTH = 400;

const LOGIN_VIEWPORT_GUTTER = 16;
const LOGIN_DEFAULT_FOOTER_HEIGHT = 56;

/** Must mirror the .login-glassCard padding breakpoints in login-screen.web.css */
const getGlassCardPadding = (width: number) =>
  width <= 420 ? 16 : width <= 768 ? 20 : width <= 900 ? 24 : 48;

const webGlassBackground = (lightTheme: boolean) =>
  lightTheme ? 'rgba(255, 255, 255, 0.42)' : 'rgba(18, 22, 38, 0.38)';

const webClassName = (className: string) => ({ className } as any);

/** RN Web injects @media rules — reliable hide in column mode when external class CSS does not apply */
const loginWebResponsiveStyles = StyleSheet.create({
  animationExtras: {
    width: '100%',
    alignItems: 'center',
    marginTop: 18,
    flex: 3,
    '@media (max-width: 900px)': {
      display: 'none',
      height: 0,
      maxHeight: 0,
      minHeight: 0,
      marginTop: 0,
      overflow: 'hidden',
      visibility: 'hidden',
      pointerEvents: 'none',
    },
  },
} as Record<string, any>);



/*TODO implement complete Google and Facebook authentication requests and tokens, remaining documentation can be found here https://github.com/expo/expo/pull/9361
import * as Google from 'expo-auth-session/providers/Google';
import * as Facebook from 'expo-auth-session/providers/Facebook';
Login Screen begins
*/

const LoginScreen = ({ navigation }) => {
  const { t } = useTranslation();
  const [loginHovered, setLoginHovered] = useState(false);
  const [registerHovered, setRegisterHovered] = useState(false);
  const [renderingView, setRenderingView] = useState(false);
  const [passwordHidden, setPasswordHidden] = useState(true);
  const { products, modalDisplayed, selectedPlan } = useSelector((state: DefaultRootState) => state.modals.registerModal);
  const flip = useSelector((state: DefaultRootState) => state.navigation?.loginFlip);
  const userState = useSelector((state: DefaultRootState) => state.promiseData);
  const registerModalState = useSelector((state: DefaultRootState) => state.modals.registerModal.modalDisplayed);
  const selectedPlanState = useSelector((state: DefaultRootState) => state.modals.registerModal.selectedPlan);
  const passwordResetModalState = useSelector((state: DefaultRootState) => state.modals?.passwordModalDisplayed);
  const appVersion = useSelector((state: DefaultRootState) => state.app.appVersion)
  const theme = useSelector((state: DefaultRootState) => state.app.theme.lightTheme);
  const [open, setOpen] = useState(false);
  const [departmentOpen, setDepartmentOpen] = useState(false);
  const [accessState, setAccessState] = useState(null);
  const [departmentState, setDepartmentState] = useState(null);
  const [otpCode, setOTPCode] = useState("");
  const [passwordResetEmail, setPasswordResetEmail] = useState('');
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const isNarrowLogin = Platform.OS === 'web' && windowWidth <= 900;
  const [loginFooterHeight, setLoginFooterHeight] = useState(LOGIN_DEFAULT_FOOTER_HEIGHT);
  const [loginContentHeight, setLoginContentHeight] = useState(0);
  const glassCardPadding = getGlassCardPadding(windowWidth);
  const glassCardMaxHeight = Math.max(
    0,
    Math.min(windowHeight * 0.9, windowHeight - loginFooterHeight - LOGIN_VIEWPORT_GUTTER * 2)
  );
  const glassCardMinHeight = isNarrowLogin ? Math.min(windowHeight * 0.8, glassCardMaxHeight) : 0;
  // Card has a 1px border on each side
  const glassCardContentSpace = glassCardMaxHeight - glassCardPadding * 2 - 2;
  const loginContentScale =
    Platform.OS === 'web' && loginContentHeight > glassCardContentSpace && glassCardContentSpace > 0
      ? Math.floor((glassCardContentSpace / loginContentHeight) * 1000) / 1000
      : 1;
  const [isPinReady, setIsPinReady] = useState(false);
  const maximumCodeLength = 6;
  const [awaitingPasscode, setAwaitingPasscode] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const showOtp = useSelector((state: DefaultRootState) => state.UI.showOTP);
  const loginForm = useRef(null);
  const [xyValue, setXYValue] = useState(0);
  const [inverseXYValue, setInverseXYValue] = useState(0);
  const dispatch = useDispatch();
  const slidePanelWidthRef = useRef(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  const route = useRoute();
  const { code, productId, learningMode: routeLearningMode } = route.params as { code?: { code: string }, productId?: string, learningMode?: boolean } | undefined || {};

  useFocusEffect(
    useCallback(() => {
      if (Platform.OS !== 'web') return;
      loadLoginScreenStyles();
      activateLoginArt();
    }, [])
  );

  // Comment out changelog fetcher for web
  // ChangeLogFetcher();
  if (Platform.OS !== 'web') {
    ChangeLogFetcher();
  }

  // Detect learning mode from route or URL
  useEffect(() => {
    const isLearnRoute = route.name === 'Learn';
    const hasLearningParam = routeLearningMode === true;

    // Check URL params on web
    let hasUrlParam = false;
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      hasUrlParam = urlParams.get('learning') === 'true' ||
        urlParams.get('learn') === 'true' ||
        window.location.pathname.includes('/learn');
    }

    const shouldEnableLearningMode = isLearnRoute || hasLearningParam || hasUrlParam;

    if (shouldEnableLearningMode) {
      dispatch({ type: SET_LEARNING_MODE });
      // Automatically set menu to 'learning' - this is handled by navigationReducer
      dispatch({ type: CHANGE_MENU, payload: 'learning' });
    } else {
      dispatch({ type: CLEAR_LEARNING_MODE });
    }
  }, [route.name, routeLearningMode, dispatch]);

  // Detect and store business prompt from URL query parameter
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const prompt = urlParams.get('prompt');

      if (prompt) {
        // Decode the prompt and store it in Redux
        const decodedPrompt = decodeURIComponent(prompt);
        console.log('📝 [LoginScreen] Detected business prompt from URL:', decodedPrompt);
        dispatch({ type: STORE_BUSINESS_PROMPT, payload: decodedPrompt });
      }
    }
  }, [dispatch]);

  const translateX = useRef(new Animated.Value(0)).current;
  const inverseTranslateX = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    translateX.setValue(xyValue);
  }, [translateX, xyValue]);

  useEffect(() => {
    inverseTranslateX.setValue(inverseXYValue);
  }, [inverseTranslateX, inverseXYValue]);

  const landingAnimationSource = useRef(
    theme
      ? require('@assets/animations/landing_Animation_dark.json')
      : require('@assets/animations/landing_Animation_light.json')
  ).current;

  const [showLandingLottie, setShowLandingLottie] = useState(Platform.OS !== 'web');

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const mountLottie = () => setShowLandingLottie(true);
    if (typeof requestIdleCallback === 'function') {
      const idleId = requestIdleCallback(mountLottie, { timeout: 2500 });
      return () => cancelIdleCallback(idleId);
    }
    const timerId = setTimeout(mountLottie, 1200);
    return () => clearTimeout(timerId);
  }, []);
    //Switch Variables
  const [isUserOrganisation, setIsUserOrganisation] = useState(false);
  const [loaded, setLoaded] = useState(true);
  const toggleSwitch = () => setIsUserOrganisation(!isUserOrganisation);
    //TODO #43 Create dynamic state for access state that will be picked up from the component
  const [items, setItems] = useState([{ label: 'SuperAdmin', value: 'SuperAdmin' }, { label: 'Admin', value: 'Admin' }]);
  const [departmentItems, setDepartmentItems] = useState([{ label: 'Management', value: 'Management' }, { label: 'Sales', value: 'Sales' }, { label: 'Finance', value: 'Finance' }, { label: 'Administration', value: 'Administration' }]);
  const [showTypeSelection, setShowTypeSelection] = useState(true);
  const loggedOut = useSelector((state: DefaultRootState) => state.app.loggedOut);
  const learningMode = useSelector((state: DefaultRootState) => state.app.learningMode);
  const registerForm = useRef(null);
  const [isChecked, setChecked] = useState(false);
  const [submitHovered, setSubmitHovered] = useState(false);
  const [switchLogin, setSwitchLogin] = useState(true);
  const [showSwipeHelp, setShowSwipeHelp] = useState(true);
  const [codeVerified, setCodeVerified] = useState<any>(null);
  const [codeVerifyLoading, setCodeVerifyLoading] = useState(false);
  const [codeVerifyError, setCodeVerifyError] = useState<string | null>(null);
  const [showPromoEntry, setShowPromoEntry] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [manualPromoCode, setManualPromoCode] = useState<string | undefined>(undefined);

  const affiliateCode = useMemo(() => {
    let rawCode: string | undefined;
    if (manualPromoCode) {
      rawCode = manualPromoCode;
    } else if (typeof code === 'string') {
      rawCode = code;
    } else if (code && typeof code === 'object' && typeof (code as any).code === 'string') {
      rawCode = (code as any).code;
    } else if (Platform.OS === 'web' && typeof window !== 'undefined') {
      // On web, read affiliate/promo from URL — but not Google OAuth ?code= (always paired with state=)
      const params = new URLSearchParams(window.location.search);
      if (!params.has('state')) {
        rawCode = params.get('code') || params.get('promoCode') || undefined;
      }
    }

    if (rawCode && typeof rawCode === 'string') {
      // Sanitize: remove any trailing artifacts like ?? or other non-alphanumeric characters
      // that might be appended during deep link redirects
      return rawCode.trim().replace(/[?#]+$/, '');
    }
    return undefined;
  }, [code, manualPromoCode]);

  const promoCards = useMemo(
    () => ([
      {
        title: 'Build your own AI workforce',
        subtitle: 'Spin up specialist agents to handle sales, ops, research, and more.',
        image: LOGIN_HERO_BG,
      },
      {
        title: 'Superpower your AI',
        subtitle: 'Connect tools, context, and workflows to get real outcomes—not just answers.',
        image: require('@assets/backgrounds/cityscape-large.png'),
      },
      {
        title: 'Turn your skills into a business',
        subtitle: 'Package expertise into repeatable systems, services, and automations.',
        image: LOGIN_HERO_BG,
      },
    ]),
    []
  );

  const promoCardWidth = PROMO_CARD_WIDTH;

  const getDiscountLabel = useCallback(() => {
    const data: any = codeVerified;
    if (!data) return 'Discount applied';

    if (typeof data?.trialDays === 'number' && data.trialDays > 0) {
      return t('promoCode.trialLabel', { count: data.trialDays });
    }

    const percent = data?.percent_off ?? data?.percentOff ?? data?.coupon?.percent_off ?? data?.coupon?.percentOff;
    if (typeof percent === 'number') return `${Math.round(percent)}% off`;

    const amountOff = data?.amount_off ?? data?.amountOff ?? data?.coupon?.amount_off ?? data?.coupon?.amountOff;
    const currency = (data?.currency ?? data?.coupon?.currency ?? 'gbp') as string;
    if (typeof amountOff === 'number') {
      const symbol = currency.toLowerCase() === 'usd' ? '$' : currency.toLowerCase() === 'eur' ? '€' : '£';
      return `${symbol}${(amountOff / 100).toFixed(0)} off`;
    }

    const sharedBy = data?.sharedBy?.name || data?.shared_by?.name;
    if (sharedBy) return `Shared by ${sharedBy}`;

    const generic = data?.discount ?? data?.label ?? data?.message;
    if (typeof generic === 'string' && generic.trim().length > 0) return generic;
    return 'Discount applied';
  }, [codeVerified, t]);

  // Promo carousel: manual swipe only on web (CSS controls visibility by breakpoint)

    const [showCookiePolicy, setShowCookiePolicy] = useState(false);
  const [notifyUser, setNotifyUser] = useState(false); // Only set to true when productId is present
  const [validatedProduct, setValidatedProduct] = useState(null); // Store validated product details
  const [showMinimizedNotification, setShowMinimizedNotification] = useState(false); // Show circle indicator

    // New user signup flow states
    const [showPlanSelection, setShowPlanSelection] = useState(false);
    const [newUserData, setNewUserData] = useState(null);
    const [isAnimatingContent, setIsAnimatingContent] = useState(false);

    // Plan data states
    const [plans, setPlans] = useState([]);
    const [plansLoading, setPlansLoading] = useState(false);
    const [plansError, setPlansError] = useState(null);

    // Animation refs for plan selection
  const planSelectionAnim = useRef(new Animated.Value(0)).current;
    
    const dynamicContentModalRef = useRef(null);

    // Check cookie policy acceptance on mount — defer UI so login inputs stay focusable.
    useEffect(() => {
      if (Platform.OS === 'web') {
        const cookieAccepted = localStorage.getItem('smartsaas-cookie-policy-accepted');
        if (!cookieAccepted) {
          return undefined;
        }
      }
    }, []);

    const handleCookieAccept = () => {
      if (Platform.OS === 'web') {
        localStorage.setItem('smartsaas-cookie-policy-accepted', 'true');
        localStorage.setItem('smartsaas-cookie-policy-timestamp', new Date().toISOString());
      }
      setShowCookiePolicy(false);
    };

    const handleCookieDecline = () => {
      if (Platform.OS === 'web') {
        localStorage.setItem('smartsaas-cookie-policy-accepted', 'false');
        localStorage.setItem('smartsaas-cookie-policy-timestamp', new Date().toISOString());
        // Optionally disable analytics/tracking here
      }
      setShowCookiePolicy(false);
    };

    useEffect(() => {
    const verifyCode = async () => {
      if (!affiliateCode) return;
      setCodeVerifyLoading(true);
      setCodeVerifyError(null);
      let isDuplicateError = false;
      try {
        const res = await apis.verifyCouponCode(affiliateCode as any);
        const payload = res?.data ?? res;
        const data = payload?.data ?? payload;
        
        // Handle nested data structures correctly (as seen in user response)
        const finalData = (data && data.data) ? data.data : data;

        if (finalData && (finalData.coupon || finalData.percent_off || finalData.amount_off || finalData.id || finalData.code)) {
          setCodeVerified(finalData);
          setCodeVerifyError(null);
          // Held so post-signup onboarding can apply trial codes / checkout can apply discounts.
          dispatch({ type: UPDATE_HELD_PROMO_CODE, payload: finalData.code || affiliateCode });
        } else {
          setCodeVerified(null);
          setCodeVerifyError('Could not verify code');
        }
      } catch (error: any) {
        isDuplicateError = !!(error?.isDuplicate || error?.message?.includes('Duplicate request prevented'));
        if (isDuplicateError) {
          // If this is a duplicate, another request is already handling it.
          // We return without setting an error so the UI stays in loading or success state.
          return;
        }
        console.warn("Affiliate code verification failed:", error?.message || error);
        setCodeVerified(null);
        const errMsg = error?.response?.data?.error || error?.message || 'Invalid or expired code';
        setCodeVerifyError(typeof errMsg === 'string' ? errMsg : 'Invalid or expired code');
      } finally {
        // ALWAYS clear loading unless it's a duplicate that we expect another call to finish.
        // Actually, safer to always clear it for this component instance.
        if (!isDuplicateError) {
          setCodeVerifyLoading(false);
        }
      }
    };

      if (affiliateCode) {
        verifyCode();
      } else {
        setCodeVerified(null);
        setCodeVerifyLoading(false);
        setCodeVerifyError(null);
      }
    }, [affiliateCode, dispatch])

  // Set notifyUser to true only once when productId is present and validated
  useEffect(() => {
    const validateAndNotifyProduct = async () => {
      if (productId) {
        try {
          const response = await apis.getProductById(productId);

          // Check if the API response indicates the product exists
          if (response && (response.success || response.data)) {
            const productData = response.data || response;
            const productName = productData.name || productData.product_name || productData.title || productId;

            setValidatedProduct({
              id: productId,
              name: productName,
              ...productData
            });
            setNotifyUser(true);
          } else {
            console.warn("⚠️ Product validation failed - product not found:", productId);
            setValidatedProduct(null);
            setNotifyUser(false);
          }
        } catch (error) {
          console.error("❌ Error validating product:", error);
          console.warn("Product ID", productId, "could not be validated");
          setValidatedProduct(null);
          setNotifyUser(false);
        }
      } else {
        setValidatedProduct(null);
        setNotifyUser(false);
      }
    };

    validateAndNotifyProduct();
  }, [productId]); // Only run when productId changes (including initial load)

    // Fetch plans only when plan selection UI is shown (not on initial login mount).
    useEffect(() => {
      if (!showPlanSelection) return;

      const fetchPlans = async () => {
        setPlansLoading(true);
        setPlansError(null);

        try {
          const response = await apis.getPlanSubscriptions();

          if (response.success && response.data) {
            // The new API returns comprehensive plans with both product and subscription options
            const comprehensivePlans = response.data;
            // Transform the comprehensive plans to match the existing UI format
            const transformedPlans = comprehensivePlans.map((plan, index) => {
              return {
                id: plan.id,
                name: plan.name,
                price: plan.price,
                period: plan.period,
                features: plan.features,
                popular: plan.popular,
                color: plan.color,
                description: plan.description,
                priceId: plan.primary_price_id,
                productId: plan.stripe_product_id,
                isActive: plan.active,

                // Add comprehensive pricing options for future use
                pricingOptions: plan.pricing,
                hasMonthly: plan.has_monthly,
                hasYearly: plan.has_yearly,
                hasOneTime: plan.has_one_time,
                tier: plan.tier
              };
            });

            setPlans(transformedPlans);
            console.log('📋 Plan details:', transformedPlans.map(p => ({
              name: p.name,
              price: p.price,
              tier: p.tier,
              hasMonthly: p.hasMonthly,
              hasYearly: p.hasYearly,
              hasOneTime: p.hasOneTime
            })));
          } else {
            throw new Error('Invalid response format from server');
          }
        } catch (error) {
          console.error('❌ Error fetching plans:', error);
          setPlansError(error.message || 'Failed to load plans');

          // Fallback to default plans
          const fallbackPlans = [
            {
              id: 'fallback-free',
              name: 'Free Plan',
              price: 'Free',
              period: '',
              features: [
                '✅ Basic features',
                '✅ Email support',
                '✅ Community access'
              ],
              popular: false,
              color: '#10B981'
            }
          ];
          setPlans(fallbackPlans);
        } finally {
          setPlansLoading(false);
        }
      };

      fetchPlans();
    }, [showPlanSelection]);

    //TODO remove looping state store !
    /* Redirecting user once data is fetched and data is not null and isAuthenticated = true */
    /* Redirect on Session retrieval */
    useEffect(() => {
      if (!loggedOut && userState?.data?.userData?.id) {
        // Check if we're already on Dashboard to avoid unnecessary navigation
        try {
          const navState = navigation.getState?.();
          const currentRoute = navState?.routes?.[navState?.index]?.name;
          if (currentRoute === 'Dashboard') {
            return;
          }
        } catch (e) {
          // Navigation state might not be available, continue
        }

        fetchAndValidateSession({
          dispatch,
          navigation,
          loggedOut,
          userId: userState.data.userData.id,
        }).then((result) => {
          setRenderingView(false);
          setXYValue(1);
        }).catch(() => {
          // Keep session failures silent on login screen.
        });
      }
    }, [userState, loggedOut]);

    /* use custom hook to handle form values and handle form submission value */
    const formLoginContext = useForm({
      initialValues: {
        email: null,
        password: null,
      },
      onSubmit: async (formValues) => {
        setTimeout(() => { setShowForgotPassword(true); }, 1000);
        // Learning mode will be automatically included from Redux state
        dispatch(loginUser(formValues, (data: { id: undefined; success: boolean }) => {
          if (data?.id !== undefined) {
            console.log('no data stored in id')
          }
        }));
      },
    })

    /** After WebAuthn + `/api/passkey/authenticate` succeeds (used by Registration’s passkey button). */
    const onPasskeyLoginSuccess = useCallback(
      async (result: { success?: boolean; data?: { userData?: { id?: string } } }) => {
        if (!result?.success || !result?.data) return;
        try {
          await completePasskeyLogin(dispatch, result);
          const userId = getPasskeyUserId(result);
          if (userId) {
            await fetchAndValidateSession({
              dispatch,
              navigation,
              loggedOut,
              userId,
            });
            setRenderingView(false);
            setXYValue(1);
            enableCapture().catch(console.warn);
          }
        } catch (error) {
          if (__DEV__) console.warn('[onPasskeyLoginSuccess]', error);
        }
      },
      [dispatch, navigation, loggedOut],
    );

    const handleFlipCard = useCallback(() => {
      dispatch({ type: LOGIN_FLIP });
    }, [dispatch]);

    const handleLoginKeyDown = async (e: { nativeEvent: { key: string; }; }, formValues: any, data) => {
      if (e.nativeEvent.key == "Enter") {
        formLoginContext.handleSubmit;
      }
    }

    const checkedStyle = () => {
      if (!isChecked) {
        return [styles.LoginButtonDisabled, { minWidth: 200, alignSelf: 'stretch' }]
      } else if (submitHovered) {
        return [styles.LoginButtonHovered, { minWidth: 200 }]
      } else { return [styles.LoginButton, { minWidth: 200, backgroundColor: 'grey' }] }
    }


    const handleRegisterKeyDown = async (e: { nativeEvent: { key: string; }; }, formValues: any) => {
      if (e.nativeEvent.key == "Enter") {
        await dispatch(registerUser(formValues));
      }
    }

    function handleResetPress() {
      if (Platform.OS === 'web') {
        navigation.navigate('SetPassword', {
          email: formLoginContext?.values?.email || passwordResetEmail || undefined,
        });
        return;
      }
      dispatch({ type: SHOW_PASSWORD_RESET_MODAL })
    }

    function privacyPolicy() {
      WebBrowser.openBrowserAsync(
        'https://smartsaas.co.uk/privacy-policy/'
      )
    }

    function termsAndConditions() {
      WebBrowser.openBrowserAsync(
        'https://smartsaas.co.uk/terms-and-conditions/'
      )
    }

    function handleAndroidPlayStore() {
      WebBrowser.openBrowserAsync(
        'https://play.google.com/store/apps/details?id=com.anonymous.smartsaas&gl=GB'
      )
    }

    function handleAppleAppStore() {
      Alert.alert('iOS coming soon!', 'Not to worry you can request earlier access by dropping us a message')
    }

    const DropDowns = () => {
      if (isUserOrganisation) {
        return (
          <View style={{ flex: 1 }}>
            <View style={{ minHeight: open ? 150 : 90, alignSelf: 'stretch' }}>
              <DropSelect zIndex={3000}
                zIndexInverse={1000} value={accessState} name={'access'} setValue={setAccessState} open={open}
                items={items} setOpen={setOpen} setItems={setItems} placeholder={`${t('accessLevel')}`} formContext={undefined} />
            </View>
            <View style={{ minHeight: departmentOpen ? 300 : 90, alignSelf: 'stretch' }}>
              <DropSelect zIndex={2000}
                zIndexInverse={2000} formContext={undefined} value={departmentState} name={'department'} setValue={setDepartmentState} open={departmentOpen}
                items={departmentItems} setOpen={setDepartmentOpen} setItems={setDepartmentItems} placeholder={'Department'} />
            </View>
          </View>
        )
      } else {
        return null
      }
    }

    const inverseCheckedStyle = () => {
      if (!isChecked) {
        return [styles.BackToLogin, { flex: 1, minWidth: 200 }]
      } else if (submitHovered) {
        return [styles.LoginButton, { minWidth: 200 }]
      } else { return [styles.LoginButtonHovered, { minWidth: 200 }] }
    }

    const handleLoginSwitch = useCallback((value) => {
      if (switchLogin) {
        Animated.timing(translateX, {
          toValue: 1,
          duration: 600,
          useNativeDriver: false
        }).start();
      } else {
        Animated.timing(inverseTranslateX, {
          toValue: 1,
          duration: 600,
          useNativeDriver: false
        }).start(({ finished }) => {
          setSwitchLogin(value)
        });
      }
      setSwitchLogin(value);
    }, [loaded])

    const gesture = (e) => {
      const { nativeEvent } = e;
      if (nativeEvent.translationX < -1) {
        setInverseXYValue(Math.abs(nativeEvent.translationX / 12));
      }
      if (nativeEvent.translationX > 1) {
        setXYValue(nativeEvent.translationX / 12);
      }
      if (nativeEvent.translationX < -150) {
        setXYValue(1);
        handleLoginSwitch(true);
      }
      else if (nativeEvent.translationX > 150) {
        setInverseXYValue(1);
        handleLoginSwitch(false);
      }
    };

    const ShowValidationOtp = () => {
      return (
        <View style={{ flex: 1 }}>
          <View style={{ flex: 1, alignSelf: 'center', top: 50, alignItems: 'center' }}>
            <FontAwesome6 name="shield-halved" size={64} color={theme ? '#4F46E5' : '#818CF8'} />
            <Text adjustsFontSizeToFit style={{ fontSize: 14, fontWeight: '400', textAlign: 'center' }}>{t('Enter your One Time Password')}</Text>
          </View>
          <>
            <OTPInput />
          </>
        </View >
      )
    }

    async function handleUserData(res) {
      let amendedData = res.data.data;

      if (Platform.OS !== 'web') {
        let activeTokens = amendedData.notification_info?.map(item => item.notification_token) || [];

          // TODO: Get notification token from appropriate service
          const notificationToken = ''; // Placeholder - should get actual token
          if (!activeTokens.length || activeTokens.includes(notificationToken)) {
            await apis.updateNotificationToken(notificationToken, amendedData.userData.id);
          }

        amendedData.expireAt = Date.now() + 1000 * 60 * 60 * 24 * 7;
      }
      return amendedData;
    }

    const proceedToPlanSelection = useCallback((userData) => {
      setNewUserData(userData);
      setIsAnimatingContent(true);
      Animated.timing(planSelectionAnim, {
        toValue: 1,
        duration: 600,
        useNativeDriver: false,
      }).start(() => {
        setShowPlanSelection(true);
        setIsAnimatingContent(false);
      });
    }, [planSelectionAnim]);

    // Handle new user signup callback — email OTP is not wired on the backend yet
    const handleNewUserSignup = useCallback((userData) => {
      proceedToPlanSelection(userData);
    }, [proceedToPlanSelection]);

    // Handle plan selection completion — sign in then match OAuth new-user → EasyMode onboarding
    const handlePlanSelectionComplete = useCallback((selectedPlan) => {
      if (!newUserData?.email || !newUserData?.password) {
        dispatch({
          type: SHOW_AUTH_MODAL,
          data: {
            content: t('emailSignupCredentialsIncomplete'),
            type: 'error',
          },
        });
        return;
      }

      // Set onboarding before SUCCESS so fetchAndValidateSession cannot race to Welcome
      dispatchPostAuthEasyModeOnboarding(dispatch);

      dispatch(
        loginUser(
          { email: newUserData.email, password: newUserData.password },
          async (data, success) => {
            if (!success || !data?.userData?.id) {
              dispatch({ type: SKIP_ONBOARDING });
              return;
            }

            await navigatePostEmailSignupToEasyMode(
              dispatch,
              navigation,
              { ...data, success: true },
              { selectedPlan: selectedPlan ?? undefined }
            );

            setShowPlanSelection(false);
            setNewUserData(null);
            setIsAnimatingContent(false);
            planSelectionAnim.setValue(0);
          }
        )
      );
    }, [dispatch, navigation, newUserData, planSelectionAnim, t]);

    // Handle plan selection cancel/back
    const handlePlanSelectionCancel = useCallback(() => {
      // Animate back to original state
      Animated.timing(planSelectionAnim, {
        toValue: 0,
        duration: 600,
        useNativeDriver: false,
      }).start(() => {
        setShowPlanSelection(false);
        setNewUserData(null);
      });
    }, [planSelectionAnim]);

    // Plan Selection Component
    const PlanSelectionPanel = () => {
      return (
        <Animated.View style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 30, 30, 0.98)',
          transform: [{
            translateX: planSelectionAnim.interpolate({
              inputRange: [0, 1],
              outputRange: [slidePanelWidthRef.current, 0],
            })
          }],
          zIndex: 1000,
        }}>
          <View style={{
            flex: 1,
            padding: WEB_LAYOUT.spacing.xl,
            paddingTop: WEB_LAYOUT.spacing.xxl,
          }}>
            {/* Header */}
            <View style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: WEB_LAYOUT.spacing.xl,
            }}>
              <TouchableOpacity
                onPress={handlePlanSelectionCancel}
                style={{
                  padding: WEB_LAYOUT.spacing.sm,
                  borderRadius: 8,
                  backgroundColor: theme ? '#F3F4F6' : '#374151',
                }}
              >
                <FontAwesome6
                  name="arrow-left"
                  size={WEB_LAYOUT.components.iconSize}
                  color={theme ? '#6B7280' : '#D1D5DB'}
                />
              </TouchableOpacity>
              <H2 style={{
                fontSize: WEB_LAYOUT.typography.xxl,
                fontWeight: '700',
                color: theme ? '#1F2937' : '#F9FAFB',
                textAlign: 'center',
                flex: 1,
              }}>
                {t('An Exclusive Offer Just for You!')}
              </H2>
              <View style={{ width: WEB_LAYOUT.components.iconSize + WEB_LAYOUT.spacing.sm * 2 }} />
            </View>

            {/* Welcome Message */}
            <View style={{
              backgroundColor: theme ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.2)',
              padding: WEB_LAYOUT.spacing.lg,
              borderRadius: 12,
              marginBottom: WEB_LAYOUT.spacing.xl,
            }}>
              <Text style={{
                fontSize: WEB_LAYOUT.typography.base,
                color: theme ? '#1F2937' : '#F9FAFB',
                textAlign: 'center',
                lineHeight: WEB_LAYOUT.typography.base * 1.4,
              }}>
                🎉 Welcome to SmartSaaS, {newUserData?.fullName || 'there'}!
                {'\n'}Select a plan to get started. Payment instructions will be sent to your email if you choose a paid plan.
              </Text>
            </View>

            {/* Plans */}
            <ScrollView showsVerticalScrollIndicator={false} style={{ flex: 1 }}>
              {plansLoading ? (
                <View style={{
                  flex: 1,
                  justifyContent: 'center',
                  alignItems: 'center',
                  paddingVertical: WEB_LAYOUT.spacing.xxl,
                }}>
                  <Text style={{
                    fontSize: WEB_LAYOUT.typography.lg,
                    color: theme ? '#6B7280' : '#9CA3AF',
                    marginBottom: WEB_LAYOUT.spacing.md,
                    textAlign: 'center',
                  }}>
                    {t('Loading pricing plans...')}
                  </Text>
                  <View style={{
                    width: 40,
                    height: 40,
                    borderRadius: 20,
                    borderWidth: 3,
                    borderColor: theme ? '#E5E7EB' : '#374151',
                    borderTopColor: '#3B82F6',
                    // Note: CSS animation doesn't work in React Native, you'd need to use Animated
                  }} />
                </View>
              ) : plansError ? (
                <View style={{
                  flex: 1,
                  justifyContent: 'center',
                  alignItems: 'center',
                  paddingVertical: WEB_LAYOUT.spacing.xxl,
                }}>
                  <Text style={{
                    fontSize: WEB_LAYOUT.typography.lg,
                    color: '#EF4444',
                    textAlign: 'center',
                    marginBottom: WEB_LAYOUT.spacing.lg,
                    fontWeight: '600',
                  }}>
                    {t('Failed to load pricing plans')}
                  </Text>
                  <Text style={{
                    fontSize: WEB_LAYOUT.typography.base,
                    color: theme ? '#6B7280' : '#9CA3AF',
                    textAlign: 'center',
                    marginBottom: WEB_LAYOUT.spacing.lg,
                  }}>
                    {plansError}
                  </Text>
                  <TouchableOpacity
                    onPress={() => {
                      setPlansError(null);
                      setPlansLoading(true);
                      // The useEffect will automatically re-run
                    }}
                    style={{
                      backgroundColor: '#3B82F6',
                      paddingHorizontal: WEB_LAYOUT.spacing.lg,
                      paddingVertical: WEB_LAYOUT.spacing.md,
                      borderRadius: WEB_LAYOUT.components.cardRadius,
                    }}
                    >
                      <Text style={{
                        color: 'white',
                        fontSize: WEB_LAYOUT.typography.base,
                        fontWeight: '600',
                      }}>
                        {t('Try Again')}
                      </Text>
                    </TouchableOpacity>
                  </View>
                ) : (
                  <View style={{
                    paddingBottom: WEB_LAYOUT.spacing.xl,
                  }}>
                    {/* One-Time Purchases Section */}
                    {plans.filter(plan => plan.hasOneTime && !plan.hasMonthly && !plan.hasYearly).length > 0 && (
                      <>
                        <View style={{
                          marginBottom: WEB_LAYOUT.spacing.lg,
                          alignItems: 'center',
                        }}>
                          <LinearGradient
                            colors={theme ? ['#667eea', '#764ba2'] : ['#4a90e2', '#357abd']}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 0 }}
                            style={{
                              paddingHorizontal: WEB_LAYOUT.spacing.xl,
                              paddingVertical: WEB_LAYOUT.spacing.md,
                              borderRadius: 25,
                              shadowColor: theme ? '#667eea' : '#4a90e2',
                              shadowOffset: { width: 0, height: 4 },
                              shadowOpacity: 0.3,
                              shadowRadius: 8,
                              elevation: 8,
                              }}
                            >
                              <Text style={{
                                fontSize: WEB_LAYOUT.typography.lg,
                                fontWeight: '700',
                                color: 'white',
                                textAlign: 'center',
                                textShadowColor: 'rgba(0,0,0,0.3)',
                                textShadowOffset: { width: 1, height: 1 },
                                textShadowRadius: 2,
                              }}>
                                {t('💎 One-Time Purchases (Paid annually)')}
                              </Text>
                            </LinearGradient>
                          </View>
                          <View
                            {...webClassName('login-planGrid')}
                            style={{
                            gap: WEB_LAYOUT.spacing.lg,
                            marginBottom: WEB_LAYOUT.spacing.xl,
                            justifyContent: 'center',
                          }}>
                            {plans.filter(plan => plan.hasOneTime && !plan.hasMonthly && !plan.hasYearly).map((plan) => (
                              <View
                                key={plan.id}
                                {...webClassName('login-planCard')}
                              >
                                <LinearGradient
                                  colors={plan.popular
                                    ? ['#FF6B6B', '#4ECDC4', '#45B7D1']
                                    : ['#667eea', '#764ba2', '#f093fb']
                                  }
                                  start={{ x: 0, y: 0 }}
                                  end={{ x: 1, y: 1 }}
                                  style={{
                                    padding: 4,
                                    borderRadius: 20,
                                    shadowColor: plan.popular ? '#FF6B6B' : '#667eea',
                                    shadowOffset: { width: 0, height: 8 },
                                    shadowOpacity: 0.4,
                                    shadowRadius: 16,
                                    elevation: 12,
                                  }}
                                >
                                  <TouchableOpacity
                                    onPress={() => handlePlanSelectionComplete(plan)}
                                    style={{
                                  backgroundColor: theme ? '#FFFFFF' : '#1F2937',
                                  borderRadius: 16,
                                  padding: WEB_LAYOUT.spacing.lg,
                                  position: 'relative',
                                  ...Platform.select({
                                    web: {
                                      boxShadow: plan.popular ?
                                        `0 8px 32px ${plan.color}40` :
                                        '0 4px 16px rgba(0, 0, 0, 0.1)',
                                    },
                                    default: {
                                      shadowColor: plan.color,
                                      shadowOffset: { width: 0, height: plan.popular ? 8 : 4 },
                                      shadowOpacity: plan.popular ? 0.3 : 0.1,
                                      shadowRadius: plan.popular ? 16 : 8,
                                      elevation: plan.popular ? 12 : 6,
                                    }
                                  }),
                                }}
                                  >
                                    {plan.popular && (
                                      <View style={{
                                        position: 'absolute',
                                        top: -12,
                                        left: WEB_LAYOUT.spacing.lg,
                                        right: WEB_LAYOUT.spacing.lg,
                                        backgroundColor: plan.color,
                                        borderRadius: 20,
                                        paddingVertical: 6,
                                        paddingHorizontal: 12,
                                        alignItems: 'center',
                                      }}>
                                        <Text style={{
                                          fontSize: WEB_LAYOUT.typography.sm,
                                          fontWeight: '700',
                                          color: 'white',
                                        }}>
                                          {t('MOST POPULAR')}
                                        </Text>
                                      </View>
                                    )}

                                    <View style={{ marginTop: plan.popular ? WEB_LAYOUT.spacing.sm : 0 }}>
                                      <H3 style={{
                                        fontSize: WEB_LAYOUT.typography.xl,
                                        fontWeight: '700',
                                        color: plan.color,
                                        marginBottom: WEB_LAYOUT.spacing.xs,
                                  }}>
                                        {plan.name}
                                      </H3>

                                      <XStack alignItems="baseline" space="$1" marginBottom="$4">
                                        <Text style={{
                                          fontSize: WEB_LAYOUT.typography.xxl * 1.2,
                                          fontWeight: '800',
                                          color: theme ? '#1F2937' : '#F9FAFB',
                                        }}>
                                          {plan.price}
                                        </Text>
                                        <Text style={{
                                          fontSize: WEB_LAYOUT.typography.sm,
                                          color: theme ? '#6B7280' : '#9CA3AF',
                                        }}>
                                          {plan.period}
                                        </Text>
                                      </XStack>

                                      <YStack space="$2">
                                        {plan.features.map((feature, index) => (
                                          <Text key={index} style={{
                                            fontSize: WEB_LAYOUT.typography.sm,
                                            color: theme ? '#374151' : '#D1D5DB',
                                            lineHeight: WEB_LAYOUT.typography.sm * 1.4,
                                          }}>
                                            {typeof feature === 'string' ? feature : feature.name || 'Feature included'}
                                          </Text>
                                        ))}
                                      </YStack>
                                    </View>
                                  </TouchableOpacity>
                                </LinearGradient>
                              </View>
                            ))}
                          </View>
                        </>
                      )}

                      {/* Subscription Plans Section */}
                      {plans.filter(plan => plan.hasMonthly || plan.hasYearly).length > 0 && (
                        <>
                          <View style={{
                            marginBottom: WEB_LAYOUT.spacing.lg,
                            alignItems: 'center',
                          }}>
                            <LinearGradient
                              colors={theme ? ['#f093fb', '#f5576c', '#4facfe'] : ['#667eea', '#764ba2', '#f093fb']}
                              start={{ x: 0, y: 0 }}
                              end={{ x: 1, y: 0 }}
                              style={{
                                paddingHorizontal: WEB_LAYOUT.spacing.xl,
                                paddingVertical: WEB_LAYOUT.spacing.md,
                                borderRadius: 25,
                                shadowColor: theme ? '#f093fb' : '#667eea',
                                shadowOffset: { width: 0, height: 4 },
                                shadowOpacity: 0.3,
                                shadowRadius: 8,
                                elevation: 8,
                              }}
                            >
                              <Text style={{
                                fontSize: WEB_LAYOUT.typography.lg,
                                fontWeight: '700',
                                color: 'white',
                                textAlign: 'center',
                                textShadowColor: 'rgba(0,0,0,0.3)',
                                textShadowOffset: { width: 1, height: 1 },
                                textShadowRadius: 2,
                              }}>
                                {t('🔄 Subscription Plans (Paid monthly)')}
                              </Text>
                            </LinearGradient>
                          </View>
                          <View
                            {...webClassName('login-planGrid')}
                            style={{
                            gap: WEB_LAYOUT.spacing.lg,
                            marginBottom: WEB_LAYOUT.spacing.xl,
                            justifyContent: 'center',
                          }}>
                            {plans.filter(plan => plan.hasMonthly || plan.hasYearly).map((plan) => (
                              <View
                                key={plan.id}
                                {...webClassName('login-planCard')}
                              >
                                <LinearGradient
                                  colors={plan.popular
                                    ? ['#4facfe', '#00f2fe', '#43e97b']
                                    : ['#f093fb', '#f5576c', '#4facfe']
                                  }
                                  start={{ x: 0, y: 0 }}
                                  end={{ x: 1, y: 1 }}
                                  style={{
                                    padding: 4,
                                    borderRadius: 20,
                                    shadowColor: plan.popular ? '#4facfe' : '#f093fb',
                                    shadowOffset: { width: 0, height: 8 },
                                    shadowOpacity: 0.4,
                                    shadowRadius: 16,
                                    elevation: 12,
                                  }}
                                >
                                  <TouchableOpacity
                                    onPress={() => handlePlanSelectionComplete(plan)}
                                    style={{
                                      backgroundColor: theme ? '#FFFFFF' : '#1F2937',
                                      borderRadius: 16,
                                      padding: WEB_LAYOUT.spacing.lg,
                                      position: 'relative',
                                      ...Platform.select({
                                        web: {
                                          boxShadow: plan.popular ?
                                            `0 8px 32px ${plan.color}40` :
                                            '0 4px 16px rgba(0, 0, 0, 0.1)',
                                        },
                                        default: {
                                          shadowColor: plan.color,
                                          shadowOffset: { width: 0, height: plan.popular ? 8 : 4 },
                                          shadowOpacity: plan.popular ? 0.3 : 0.1,
                                          shadowRadius: plan.popular ? 16 : 8,
                                          elevation: plan.popular ? 12 : 6,
                                    }
                                  }),
                                }}
                                  >
                                    {plan.popular && (
                                      <View style={{
                                        position: 'absolute',
                                        top: -12,
                                        left: WEB_LAYOUT.spacing.lg,
                                        right: WEB_LAYOUT.spacing.lg,
                                        backgroundColor: plan.color,
                                        borderRadius: 20,
                                        paddingVertical: 6,
                                        paddingHorizontal: 12,
                                        alignItems: 'center',
                                      }}>
                                        <Text style={{
                                          fontSize: WEB_LAYOUT.typography.sm,
                                          fontWeight: '700',
                                          color: 'white',
                                        }}>
                                          {t('MOST POPULAR')}
                                        </Text>
                                      </View>
                                    )}

                                    <View style={{ marginTop: plan.popular ? WEB_LAYOUT.spacing.sm : 0 }}>
                                      <H3 style={{
                                        fontSize: WEB_LAYOUT.typography.xl,
                                        fontWeight: '700',
                                        color: plan.color,
                                        marginBottom: WEB_LAYOUT.spacing.xs,
                                      }}>
                                        {plan.name}
                                      </H3>

                                      <XStack alignItems="baseline" space="$1" marginBottom="$4">
                                        <Text style={{
                                          fontSize: WEB_LAYOUT.typography.xxl * 1.2,
                                          fontWeight: '800',
                                          color: theme ? '#1F2937' : '#F9FAFB',
                                        }}>
                                          {plan.price}
                                        </Text>
                                        <Text style={{
                                          fontSize: WEB_LAYOUT.typography.sm,
                                          color: theme ? '#6B7280' : '#9CA3AF',
                                        }}>
                                          {plan.period}
                                        </Text>
                                      </XStack>

                                      <YStack space="$2">
                                        {plan.features.map((feature, index) => (
                                          <Text key={index} style={{
                                            fontSize: WEB_LAYOUT.typography.sm,
                                            color: theme ? '#374151' : '#D1D5DB',
                                            lineHeight: WEB_LAYOUT.typography.sm * 1.4,
                                          }}>
                                            {typeof feature === 'string' ? feature : feature.name || 'Feature included'}
                                          </Text>
                                        ))}
                                      </YStack>
                                    </View>
                                  </TouchableOpacity>
                                </LinearGradient>
                              </View>
                            ))}
                          </View>
                        </>
                      )}

                      {/* Continue Free Option */}
                      <TouchableOpacity
                        onPress={() => handlePlanSelectionComplete(null)}
                        style={{
                          backgroundColor: theme ? '#F9FAFB' : '#374151',
                          borderRadius: 12,
                          padding: WEB_LAYOUT.spacing.lg,
                          borderWidth: 1,
                          borderColor: theme ? '#E5E7EB' : '#4B5563',
                          marginTop: WEB_LAYOUT.spacing.lg,
                          alignItems: 'center',
                        }}
                      >
                    <Text style={{
                      fontSize: WEB_LAYOUT.typography.base,
                      fontWeight: '600',
                      color: theme ? '#6B7280' : '#D1D5DB',
                      textAlign: 'center',
                    }}>
                      {t('Continue with Free Tier')}
                    </Text>
                    <Text style={{
                      fontSize: WEB_LAYOUT.typography.sm,
                      color: theme ? '#9CA3AF' : '#9CA3AF',
                      textAlign: 'center',
                      marginTop: 4,
                    }}>
                      {t('You can upgrade anytime')}
                    </Text>
                  </TouchableOpacity>
                </View>
              )}
            </ScrollView>
          </View>
        </Animated.View>
      );
    };



    return (
      <YStack
        nativeID="login-root"
        {...webClassName(`login-root ${theme ? 'login-root--light' : 'login-root--dark'}`)}
        flex={1}
        width="100%"
        minWidth={320}
        style={{
        position: 'relative',
        overflow: 'hidden',
        justifyContent: 'center',
        alignItems: 'center',
        height: windowHeight,
        maxHeight: windowHeight,
        paddingBottom: Platform.OS === 'web' ? loginFooterHeight : 0,
      } as any}>
        {Platform.OS === 'web' ? <LoginArtBackground /> : null}
        <PasswordResetModal emailForward={formLoginContext.values.email} />
        {/* Main Container — column-mode scroll handled by login-screen.web.css */}
          <YStack
            {...webClassName('login-scrollContainer')}
            flex={isNarrowLogin ? undefined : 1}
            width={isNarrowLogin ? ('85vw' as any) : '100%'}
            maxWidth={isNarrowLogin ? undefined : 1280}
            minWidth={isNarrowLogin ? ('85vw' as any) : undefined}
            style={{
              zIndex: 10,
              position: 'relative',
              margin: '0 auto',
              ...(isNarrowLogin
                ? {
                    width: '85vw',
                    minWidth: '85vw',
                    maxWidth: '85vw',
                    minHeight: '80vh',
                    height: 'auto',
                    maxHeight: '90vh',
                  }
                : {
                    height: '100%' as any,
                    maxHeight: '100%' as any,
                    minHeight: 0,
                  }),
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'hidden',
            } as any}
          >
            {/* Glass Card Container - frosted blur over ribbon art */}
            <BlurView
              intensity={theme ? 58 : 48}
              tint={theme ? 'light' : 'dark'}
              {...webClassName('login-glassCard')}
              style={{
                width: isNarrowLogin ? ('85vw' as any) : '100%',
                maxWidth: isNarrowLogin ? ('85vw' as any) : 1200,
                minWidth: isNarrowLogin ? ('85vw' as any) : 320,
                borderRadius: isNarrowLogin ? 20 : 24,
                height: 'auto' as any,
                maxHeight: glassCardMaxHeight,
                minHeight: glassCardMinHeight,
                overflow: 'hidden',
                backgroundColor: webGlassBackground(theme),
                padding: glassCardPadding,
                top: 0,
                justifyContent: 'flex-start',
                flexGrow: 0,
                flexShrink: 0,
                alignSelf: 'center',
                marginVertical: isNarrowLogin ? 0 : ('auto' as any),
                borderWidth: 1,
                borderColor: theme ? 'rgba(255, 255, 255, 0.45)' : 'rgba(255, 255, 255, 0.14)',
                boxShadow: theme
                  ? '0 8px 40px rgba(15, 23, 42, 0.10), inset 0 1px 0 rgba(255,255,255,0.55)'
                  : '0 8px 40px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255,255,255,0.08)',
              } as any}
            >
              {/* Plain View: RN Web onLayout reports offsetHeight, which ignores the scale transform */}
              <View
                onLayout={(e) => setLoginContentHeight(e.nativeEvent.layout.height)}
                style={{
                  width: '100%',
                  flexGrow: 0,
                  flexShrink: 0,
                  justifyContent: 'center',
                  transform: [{ scale: loginContentScale }],
                  transformOrigin: 'top center',
                } as any}
              >
              {/* Flex Row Layout - Animation and Form Side by Side */}
            <XStack
              nativeID="login-mainRow"
              flexWrap="wrap"
              {...webClassName('login-mainRow')}
              style={{
                alignItems: 'center',
                gap: "10%",
                width: '100%',
                minHeight: 0,
                flexGrow: 0,
                flexShrink: 0,
              }}>
                {/* Lottie Animation Section - Left Side */}
                <View
                  {...webClassName('login-animationSection')}
                  style={{
                  flex: 1,
                  minWidth: "50%",
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  alignSelf: 'center',
                  minHeight: '10vh' as any
              } as any}
                >
                  <View
                    {...webClassName('login-logo')}
                    style={[styles.loginAnimationContainer, {
                      width: '100%',
                      maxWidth: "100%",
                      height: 220,
                      maxHeight: "18vh",
                    }]}
                  >
                    {showLandingLottie ? (
                      <LottieView
                        style={styles.loginAnimationLottie}
                        source={landingAnimationSource}
                        autoPlay
                        loop={false}
                      />
                    ) : null}
                  </View>

                  {/* Promo + dynamic info — hidden in column mode via CSS (nativeID → id on web) */}
                  <View
                    nativeID="login-animationExtras"
                    {...webClassName('login-animationExtras')}
                    style={loginWebResponsiveStyles.animationExtras}
                  >
                  {affiliateCode && (
                    <View
                      style={{
                        width: '100%',
                        maxWidth: 520,
                        marginTop: 16,
                        borderRadius: 18,
                        padding: 16,
                        ...Platform.select({
                          web: {
                            backgroundColor: theme ? 'rgba(16, 185, 129, 0.14)' : 'rgba(16, 185, 129, 0.22)',
                          },
                          default: {
                            backgroundColor: theme ? 'rgba(16, 185, 129, 0.10)' : 'rgba(16, 185, 129, 0.14)',
                          },
                        }),
                        borderWidth: 1,
                        borderColor: theme ? 'rgba(16, 185, 129, 0.30)' : 'rgba(16, 185, 129, 0.35)',
                      } as any}
                    >
                      <Text style={{
                        fontSize: 14,
                        fontWeight: '800',
                        color: theme ? '#064E3B' : '#D1FAE5',
                        marginBottom: 6,
                      }}>
                        {t('Affiliate code detected')}
                      </Text>
                      <Text style={{
                        fontSize: 13,
                        fontWeight: '700',
                        color: theme ? '#065F46' : '#A7F3D0',
                        marginBottom: 10,
                      }}>
                        {`Code: ${affiliateCode}`}
                      </Text>

                      {codeVerifyLoading ? (
                        <Text style={{
                          fontSize: 13,
                          fontWeight: '600',
                          color: theme ? 'rgba(6, 95, 70, 0.75)' : 'rgba(167, 243, 208, 0.8)',
                        }}>
                          {t('Checking discount…')}
                        </Text>
                      ) : codeVerifyError ? (
                        <Text style={{
                          fontSize: 13,
                          fontWeight: '600',
                          color: theme ? '#B91C1C' : '#FCA5A5',
                        }}>
                          {codeVerifyError}
                        </Text>
                      ) : codeVerified ? (
                        <Text style={{
                          fontSize: 13,
                          lineHeight: 18,
                          fontWeight: '700',
                          color: theme ? '#064E3B' : '#ECFDF5',
                        }}>
                          {`Discount: ${getDiscountLabel()}`}
                        </Text>
                      ) : null}
                    </View>
                  )}

                    {/* Carousel visibility controlled by CSS at smaller breakpoints */}
                    <YStack
                        nativeID="login-promoCarousel"
                        className="login-promoCarousel"
                        style={{
                          width: '100%',
                          maxWidth: 620,
                          borderRadius: 18,
                          borderColor: theme ? 'rgba(255, 255, 255, 0.20)' : 'rgba(255, 255, 255, 0.12)',
                          paddingVertical: 16,
                          paddingHorizontal: 16,
                        } as any}
                      >
                        <View {...webClassName('login-promoCard')} style={{ width: '100%' }}>
                          <View
                            style={{
                              width: '100%',
                              minHeight: 210,
                              borderRadius: 18,
                              overflow: 'hidden',
                            }}
                          >
                            <View style={{
                              flex: 1,
                              padding: 18,
                              justifyContent: 'flex-end',
                              backgroundColor: theme ? 'rgba(255, 255, 255, 0.82)' : 'rgba(18, 22, 38, 0.72)',
                              borderRadius: 18,
                            }}>
                              <Text style={{
                                fontSize: 18,
                                fontWeight: '900',
                                color: theme ? '#0F172A' : '#F8FAFC',
                                marginBottom: 8,
                              }}>
                                {promoCards[0].title}
                              </Text>
                              <Text style={{
                                fontSize: 13,
                                lineHeight: 18,
                                fontWeight: '600',
                                color: theme ? 'rgba(15, 23, 42, 0.82)' : 'rgba(248, 250, 252, 0.82)',
                              }}>
                                {promoCards[0].subtitle}
                              </Text>
                            </View>
                          </View>
                        </View>
                      </YStack>
                
                    {/* Affiliate code message - shown below carousel when code is present */}
                  
                  </View>
              </View>
                {/* Registration Form Section - Right Side */}
                <View
                  {...webClassName('login-formSection')}
                  style={{
                  flex: Platform.OS === 'web' ? 0 : 3,
                  flexBasis: Platform.OS === 'web' ? 420 : undefined,
                  flexGrow: Platform.OS === 'web' ? 0 : undefined,
                  flexShrink: Platform.OS === 'web' ? 1 : undefined,
                  padding: 0,
                  rowGap: 16,
                  borderWidth: 0,
                  width: '100%',
                  maxWidth: Platform.OS === 'web' ? 420 : undefined,
                  alignItems: Platform.OS === 'web' ? 'center' : undefined,
                  alignSelf: Platform.OS === 'web' ? 'center' : undefined,
                }}
                >
                  {/* Learning Mode Indicator */}
                  {learningMode && (
                    <View style={{
                      backgroundColor: theme ? '#3B82F6' : '#2563EB',
                      borderRadius: 8,
                      padding: 12,
                      marginHorizontal: "10%",
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderWidth: 2,
                      borderColor: theme ? '#60A5FA' : '#3B82F6',
                    }}>
                      <FontAwesome6
                        name="graduation-cap"
                        size={20}
                        color="#ffffff"
                        style={{ marginRight: 8 }}
                      />
                      <Text style={{
                        color: '#ffffff',
                        fontSize: 14,
                        fontWeight: '600',
                        textAlign: 'center',
                      }}>
                        {t('Learning Mode Active - Explore without paid features')}
                      </Text>
                    </View>
                  )}
                  {/* Code Detection Visual Indicator */}
                  {codeVerified && (
                    <View style={{
                      borderRadius: 12,
                      padding: 12,
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderWidth: 1,
                      borderColor: theme ? 'rgba(16, 185, 129, 0.3)' : 'rgba(16, 185, 129, 0.4)',
                    }}>
                      <FontAwesome6
                        name="check-circle"
                        size={20}
                        color={theme ? '#059669' : '#34D399'}
                        style={{ marginRight: 8 }}
                      />
                      <Text style={{
                        color: theme ? '#064E3B' : '#ECFDF5',
                        fontSize: 14,
                        fontWeight: '700',
                        textAlign: 'center',
                      }}>
                        {typeof codeVerified?.trialDays === 'number' && codeVerified.trialDays > 0
                          ? t('promoCode.trialAppliedBanner', { code: affiliateCode, count: codeVerified.trialDays })
                          : `🎉 Coupon code "${affiliateCode}" applied successfully!`}
                      </Text>
                    </View>
                  )}
                  <Registration
                    flipCard={handleFlipCard}
                    onNewUserSignup={handleNewUserSignup}
                    code={affiliateCode ?? undefined}
                    productId={productId}
                    onPasskeyLoginSuccess={onPasskeyLoginSuccess}
                  />
                  {!codeVerified && (
                    <View style={{ width: '100%', maxWidth: 400, alignSelf: 'center', alignItems: 'center' }}>
                      {showPromoEntry ? (
                        <PromoCodeField
                          theme={theme}
                          value={promoInput}
                          onChangeText={(value) => {
                            setPromoInput(value);
                            if (manualPromoCode) setManualPromoCode(undefined);
                          }}
                          onSubmit={() => setManualPromoCode(promoInput.trim())}
                          placeholder={t('promoCode.placeholder')}
                          buttonLabel={t('promoCode.apply')}
                          loading={codeVerifyLoading}
                          status={
                            manualPromoCode && codeVerifyError
                              ? { tone: 'error', text: codeVerifyError }
                              : null
                          }
                        />
                      ) : (
                        <Pressable
                          onPress={() => setShowPromoEntry(true)}
                          accessibilityRole="button"
                          hitSlop={8}
                          style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}
                        >
                          <FontAwesome6
                            name="ticket"
                            size={responsiveFontSize(12)}
                            color={theme ? '#2563eb' : '#93c5fd'}
                          />
                          <Text
                            style={{
                              fontSize: responsiveFontSize(13),
                              fontWeight: '600',
                              color: theme ? '#2563eb' : '#93c5fd',
                            }}
                          >
                            {t('promoCode.haveCode')}
                          </Text>
                        </Pressable>
                      )}
                    </View>
                  )}
                </View>
            </XStack>
            <YStack
              nativeID="login-dynamicContentInfo"
              className="login-dynamicContentInfo"
              width="100%"
              marginTop={12}
            >
              <DynamicContentInfo modalRef={dynamicContentModalRef} />
            </YStack>
              </View>
            </BlurView>
        </YStack>

        {showPlanSelection && <PlanSelectionPanel />}

        {/* Modern Terms and Privacy Notice - Glass Morphism Style */}
        <YStack
          {...webClassName('login-footer')}
          onLayout={(e) => setLoginFooterHeight(Math.ceil(e.nativeEvent.layout.height))}
          style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
        }}>
          <YStack
            {...webClassName('login-footerInner')}
            style={{
            backgroundColor: theme ? 'rgba(255, 255, 255, 0.92)' : 'rgba(18, 22, 38, 0.92)',
            paddingHorizontal: WEB_LAYOUT.spacing.lg,
            paddingVertical: WEB_LAYOUT.spacing.md,
            borderTopWidth: 1,
            borderTopColor: theme ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
            boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}>
            <View {...webClassName('login-footerStatus')} style={{ position: 'absolute', left: WEB_LAYOUT.spacing.lg, top: 0, bottom: 0, justifyContent: 'center' }}>
              <BackendStatusIndicator theme={theme} appVersion={appVersion} />
            </View>
            {
              windowWidth > 800 && 
            <XStack
              alignItems="center"
              justifyContent="center"
                  gap="$2"
              flexWrap="wrap"
              paddingHorizontal={WEB_LAYOUT.spacing.sm}
            >
              <FontAwesome6
                name="shield-halved"
                size={WEB_LAYOUT.components.iconSize - 2}
                color="white"
                style={{ marginRight: 6 }}
              />
                  <SizableText
                    flexWrap="wrap"
                    style={{
                fontSize: WEB_LAYOUT.typography.sm,
                color: Platform.OS === 'web'
                  ? (theme ? '#1F2937' : '#F9FAFB')
                  : 'white',
                fontWeight: '500',
                lineHeight: WEB_LAYOUT.typography.sm * 1.4,
                flexShrink: 1,
                textAlign: 'center',
              }}>
                {t('By using SmartSaaS®️ Leaf, you agree to our')} {' '}
                  </SizableText>
              <Pressable
                onPress={() => WebBrowser.openBrowserAsync('https://smartsaas.co.uk/privacy-policy/')}
                style={({ pressed }) => ({
                  opacity: pressed ? 0.8 : 1,
                  transform: [{ scale: pressed ? 0.98 : 1 }],
                  paddingHorizontal: 4,
                  paddingVertical: 2,
                  borderRadius: 4,
                  backgroundColor: pressed ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                })}
              >
                <Text style={{
                  fontSize: WEB_LAYOUT.typography.sm,
                  color: Platform.OS === 'web'
                    ? (theme ? '#4F46E5' : '#818CF8')
                    : 'white',
                  fontWeight: '700',
                  textDecorationLine: 'underline',
                  textDecorationColor: Platform.OS === 'web'
                    ? (theme ? '#4F46E5' : '#818CF8')
                    : 'white',
                }}>
                  {t('privacy policy')}
                </Text>
              </Pressable>
              <Text style={{
                fontSize: WEB_LAYOUT.typography.sm,
                color: Platform.OS === 'web'
                  ? (theme ? '#1F2937' : '#F9FAFB')
                  : 'white',
                fontWeight: '500',
              }}>
                {' '}and{' '}
              </Text>
              <Pressable
                onPress={() => WebBrowser.openBrowserAsync('https://smartsaas.co.uk/terms-and-conditions/')}
                style={({ pressed }) => ({
                  opacity: pressed ? 0.8 : 1,
                  transform: [{ scale: pressed ? 0.98 : 1 }],
                  paddingHorizontal: 4,
                  paddingVertical: 2,
                  borderRadius: 4,
                  backgroundColor: pressed ? (Platform.OS === 'web' ? 'rgba(79, 70, 229, 0.1)' : 'rgba(255, 255, 255, 0.1)') : 'transparent',
                })}
              >
                <Text style={{
                  fontSize: WEB_LAYOUT.typography.sm,
                  color: Platform.OS === 'web'
                    ? (theme ? '#4F46E5' : '#818CF8')
                    : 'white',
                  fontWeight: '700',
                  textDecorationLine: 'underline',
                  textDecorationColor: Platform.OS === 'web'
                    ? (theme ? '#4F46E5' : '#818CF8')
                    : 'white',
                }}>
                  {t('terms & conditions')}
                </Text>
              </Pressable>
            </XStack>
            }
          </YStack>
        </YStack>

        {/* Cookie Policy Popover */}
        {showCookiePolicy && (
          <View
            {...webClassName('login-cookiePopover')}
            style={{
              position: 'fixed',
              zIndex: 10000,
            }}
          >
            <View style={{
              backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 30, 30, 0.98)',
              borderRadius: 16,
              padding: WEB_LAYOUT.spacing.lg,
              borderWidth: 1,
              borderColor: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            }}>
              <XStack
                alignItems="flex-start"
                gap="$3"
                flexWrap="wrap"
              >
                <YStack flex={1} gap="$2">
                  <XStack alignItems="center" justifyContent="space-between">
                    <XStack alignItems="center" gap="$2">
                      <FontAwesome6
                        name="cookie-bite"
                        size={WEB_LAYOUT.components.iconSize}
                        color={theme ? '#4F46E5' : '#818CF8'}
                      />
                      <Text style={{
                        fontSize: WEB_LAYOUT.typography.base,
                        fontWeight: '600',
                        color: theme ? '#1F2937' : '#F9FAFB',
                      }}>
                        {t('Cookie Notice')}
                      </Text>
                    </XStack>
                    <TouchableOpacity
                      onPress={handleCookieDecline}
                      style={{
                        padding: 4,
                        borderRadius: 4,
                      }}
                    >
                      <AntDesign
                        name="close"
                        size={16}
                        color={theme ? '#6B7280' : '#9CA3AF'}
                      />
                    </TouchableOpacity>
                  </XStack>
                  <Text style={{
                    fontSize: WEB_LAYOUT.typography.sm,
                    color: theme ? '#6B7280' : '#D1D5DB',
                    lineHeight: WEB_LAYOUT.typography.sm * 1.4,
                    flexShrink: 1,
                  }}>
                    We use cookies to enhance your experience, analyze site traffic, and personalize content. By continuing to use our site, you consent to our use of cookies.{' '}
                    <Pressable
                      onPress={() => WebBrowser.openBrowserAsync('https://smartsaas.co.uk/cookie-policy/')}
                      style={{ display: 'inline' }}
                    >
                      <Text style={{
                        fontSize: WEB_LAYOUT.typography.sm,
                        color: theme ? '#4F46E5' : '#818CF8',
                        fontWeight: '600',
                        textDecorationLine: 'underline',
                      }}>
                        {t('Learn more')}
                      </Text>
                    </Pressable>
                  </Text>
                </YStack>
                <XStack space="$2" alignItems="center" flexWrap="wrap">
                  <Button
                    size="$2"
                    backgroundColor="transparent"
                    onPress={() => WebBrowser.openBrowserAsync('https://smartsaas.co.uk/cookie-preferences/')}
                    pressStyle={{
                      backgroundColor: theme ? '#F9FAFB' : '#374151',
                    }}
                  >
                    <Text style={{
                      fontSize: WEB_LAYOUT.typography.xs,
                      color: theme ? '#6B7280' : '#D1D5DB',
                      fontWeight: '500',
                    }}>
                      {t('Customize')}
                    </Text>
                  </Button>
                  <Button
                    size="$2"
                    backgroundColor={theme ? '#F3F4F6' : '#374151'}
                    onPress={handleCookieDecline}
                    pressStyle={{
                      backgroundColor: theme ? '#E5E7EB' : '#4B5563',
                    }}
                  >
                    <Text style={{
                      fontSize: WEB_LAYOUT.typography.xs,
                      color: theme ? '#6B7280' : '#D1D5DB',
                      fontWeight: '500',
                    }}>
                      {t('Decline')}
                    </Text>
                  </Button>
                  <Button
                    size="$2"
                    backgroundColor={theme ? '#4F46E5' : '#818CF8'}
                    onPress={handleCookieAccept}
                    pressStyle={{
                      backgroundColor: theme ? '#4338CA' : '#6366F1',
                    }}
                  >
                    <Text style={{
                      fontSize: WEB_LAYOUT.typography.xs,
                      color: 'white',
                      fontWeight: '600',
                    }}>
                      {t('Accept All')}
                    </Text>
                  </Button>
                </XStack>
              </XStack>
            </View>
          </View>
        )}
        {
          productId && notifyUser && (
            <View
              {...webClassName('login-productNotice')}
              style={{
                position: "absolute",
                top: '20%',
                right: '9%',
                transform: [
                  { translateX: -150 },
                  { translateY: -150 }
                ],
                minWidth: 300,
                minHeight: 200,
                maxWidth: 400,
                backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 30, 30, 0.98)',
                borderRadius: 20,
                padding: WEB_LAYOUT.spacing.xl,
                zIndex: 10000,
                borderWidth: 2,
                borderColor: '#3B82F6',
                boxShadow: '0 20px 40px rgba(59, 130, 246, 0.3)',
              }}
            >
              <LinearGradient
                colors={['#4F46E5', '#3B82F6', '#06B6D4']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  borderTopLeftRadius: 18,
                  borderTopRightRadius: 18,
                }}
              />
              <YStack alignItems="center" gap="$4">
                {/* Success Icon */}
                <View style={{
                  backgroundColor: '#10B981',
                  borderRadius: 50,
                  padding: WEB_LAYOUT.spacing.md,
                  marginTop: WEB_LAYOUT.spacing.sm,
                }}>
                  <FontAwesome6
                    name="check-circle"
                    size={32}
                    color="white"
                  />
                </View>
                {/* Header */}
                <YStack alignItems="center" gap="$2">
                  <Text style={{
                    fontSize: WEB_LAYOUT.typography.xl,
                    fontWeight: '700',
                    color: theme ? '#1F2937' : '#F9FAFB',
                    textAlign: 'center',
                    lineHeight: WEB_LAYOUT.typography.xl * 1.2,
                  }}>
                    {t('🎉 Excellent Choice!')}
                  </Text>

                  <View style={{
                    backgroundColor: theme ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.2)',
                    paddingHorizontal: WEB_LAYOUT.spacing.md,
                    paddingVertical: WEB_LAYOUT.spacing.sm,
                    borderRadius: 12,
                    borderWidth: 1,
                    borderColor: '#3B82F6',
                  }}>
                    <Text style={{
                      fontSize: WEB_LAYOUT.typography.base,
                      fontWeight: '600',
                      color: '#3B82F6',
                      textAlign: 'center',
                    }}>
                      {validatedProduct?.name || productId}
                    </Text>
                  </View>
                </YStack>

                {/* Message */}
                <YStack alignItems="center" gap="$3">
                  <Text style={{
                    fontSize: WEB_LAYOUT.typography.base,
                    color: theme ? '#4B5563' : '#D1D5DB',
                    textAlign: 'center',
                    lineHeight: WEB_LAYOUT.typography.base * 1.4,
                    fontWeight: '500',
                  }}>
                    {t("You're just one step away from unlocking premium features!")}
                  </Text>

                  <View style={{
                    backgroundColor: theme ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.2)',
                    paddingHorizontal: WEB_LAYOUT.spacing.lg,
                    paddingVertical: WEB_LAYOUT.spacing.md,
                    borderRadius: 12,
                    borderWidth: 1,
                    borderColor: '#10B981',
                  }}>
                    <Text style={{
                      fontSize: WEB_LAYOUT.typography.sm,
                      color: '#10B981',
                      textAlign: 'center',
                      fontWeight: '600',
                    }}>
                      {t('💳 Complete registration to secure your purchase')}
                    </Text>
                  </View>
                </YStack>

                {/* Call to Action */}
                <TouchableOpacity
                  onPress={() => {
                    // Minimize notification to circle indicator and scroll to registration
                    setNotifyUser(false);
                    setShowMinimizedNotification(true);
                    // Optionally scroll to registration form
                  }}
                  style={{
                    backgroundColor: '#3B82F6',
                    paddingHorizontal: WEB_LAYOUT.spacing.xl,
                    paddingVertical: WEB_LAYOUT.spacing.md,
                    borderRadius: 12,
                    marginTop: WEB_LAYOUT.spacing.sm,
                    ...Platform.select({
                      web: {
                        boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                      },
                      default: {
                        shadowColor: '#3B82F6',
                        shadowOffset: { width: 0, height: 4 },
                        shadowOpacity: 0.3,
                        shadowRadius: 8,
                        elevation: 8,
                      }
                    }),
                  }}
                >
                  <XStack alignItems="center" space="$2">
                    <FontAwesome6
                      name="rocket"
                      size={16}
                      color="white"
                    />
                    <Text style={{
                      fontSize: WEB_LAYOUT.typography.base,
                      fontWeight: '600',
                      color: 'white',
                    }}>
                      {t('Log in / Create Account')}
                    </Text>
                  </XStack>
                </TouchableOpacity>
              </YStack>
            </View>
          )
        }
        {/* Minimized Product Notification - Circle Indicator */}
        {
          showMinimizedNotification && validatedProduct && (
            <View
              {...webClassName('login-productBadge')}
              style={{
                position: "absolute",
                width: 60,
                height: 60,
                borderRadius: 30,
                backgroundColor: '#3B82F6',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 9999,
                borderWidth: 3,
                borderColor: 'white',
                boxShadow: '0 8px 20px rgba(59, 130, 246, 0.4)',
              }}
            >
              <TouchableOpacity
                onPress={() => {
                  // Expand back to full notification
                  setShowMinimizedNotification(false);
                  setNotifyUser(true);
                }}
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: 30,
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <FontAwesome6
                  name="shopping-cart"
                  size={20}
                  color="white"
                />
                <View style={{
                  position: 'absolute',
                  top: -5,
                  right: -5,
                  width: 20,
                  height: 20,
                  borderRadius: 10,
                  backgroundColor: '#10B981',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderWidth: 2,
                  borderColor: 'white',
                }}>
                  <Text style={{
                    fontSize: 10,
                    color: 'white',
                    fontWeight: '700',
                  }}>
                    1
                  </Text>
                </View>
              </TouchableOpacity>
            </View>
          )
        }

      </YStack>
    );
  }

export default LoginScreen;
